import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import type { ReviewState } from '@chess/api-review';
import { useSelect } from '@chess/api-review/react';
import { useConstraint, useConstraintOwner, useConstraintRegistry } from '@chess/api-constraints/react';
import { CHESS_API, uciToInput, type Chess } from '@chess/api-chess';
import { PUZZLES_API, type Puzzles } from '@chess/api-puzzles';
import { positionAt, type ShortlistReading } from '@chess/api-review';
import { stepControlsView, type StepAction } from './controls.ts';
import { MAIN_LINE_PLIES, bookExitPly, theoryMove } from './mistake-actions.ts';
import { isTyping, keyAction } from './keys.ts';
import { StepControls } from './StepControls.tsx';
import { useReview } from './use-review.ts';
import './step-controls.css';

// The steps under the board, after v1's ReviewStepControls: a bar of two
// zones (2026-10-14; three with the steps centred before). At the left end
// the four buttons to move through the game: the inner two step, the outer
// two go to the previous and the next of the reader's mistakes, or to the
// start and the end when no mistake lies that way (outer-steps.ts,
// 2026-10-13; ↑ and ↓ do the same). At the right end the contextual exit;
// on the game's own position before one of the reader's blunders or misses,
// Puzzle (P), which sets the position up to solve through the puzzles API;
// and at any position, on the game or on a line of the
// reader's own, Main line (L), which, from a game position still in the
// book, first steps the game through its own book moves to where it left
// the book (the opening as played), then plays what the board's green
// arrow shows, the shortlist's live reading of the position (the scan's
// best move until it is in), again and again for a few moves or to a mate
// (mistake-actions.ts), leaving the reader on a line with the exit at the
// right. Main line stands beside Puzzle or the exit, never hidden by them. The arrow
// keys do the same as the centre: left and right step, up and down jump to
// either end. When the reader has left the game for a line of their own, the
// forward two are disabled while the back two stay: back takes back the
// line's last move, one at a time, so another move can be tried at any point
// (the last take-back lands on the branch ply), and start drops the line.
// The exit is then "Back to the game", which returns to the ply the line
// branched from; Escape does the same. The board's Controls constraint
// switches the directions off, or, with a range (a puzzle), makes them look
// along the path within it; the session enforces it, so the buttons call
// the usual steps and only show what the constraint allows: while an owner
// holds the controls (a puzzle) the centre draws only look back (‹) and back
// to the puzzle (↶), first, next and end left out rather than greyed, so the
// puzzle's controls read as a different mode, and the exit is "Back to the
// puzzle" while looking back. Reads the review interface and the constraint
// registry; owns nothing. What is drawn, enabled and said is decided in
// `controls.ts`, drawn by `StepControls.tsx`; what a key does is in `keys.ts`.

const IDLE: ReviewState = { status: 'idle', ply: 0 };
/** The least pause between the main line's moves as it plays out: the puzzle solution's pace; the engine's own time when longer. */
const MAIN_LINE_STEP_MS = 600;
/**
 * The most Main line waits for the engine to finish a position (2026-10-19):
 * the shortlist's search stops at depth 14 or after 4 s, a quarter second
 * after the board moves; past this the move is played at the depth reached.
 */
const MAIN_LINE_WAIT_MS = 6000;

export function setup(app: PiletApi) {
  // The shortlist's reading of the position on the board, the board's green arrow's source.
  const shortlistRef: { current: ShortlistReading } = { current: {} };
  app.on('review.shortlist', (reading: ShortlistReading) => {
    shortlistRef.current = reading;
  });

  const Panel: React.FC = () => {
    const review = useReview(app);
    const state = useSelect(review, (s) => s) ?? IDLE;
    const registry = useConstraintRegistry(app);
    const controls = useConstraint(registry, state.scope, 'controls');
    const owner = useConstraintOwner(registry, state.scope);
    const view = stepControlsView(state, controls, owner);
    const rangedRef = React.useRef(view.ranged);
    rangedRef.current = view.ranged;
    const outerRef = React.useRef(view.outer);
    outerRef.current = view.outer;
    const rightRef = React.useRef(view.zones.right);
    rightRef.current = view.zones.right;
    const mainLineRef = React.useRef(view.mainLine);
    mainLineRef.current = view.mainLine;
    // Main line: what the board's green arrow shows, played. The arrow is
    // the shortlist's live reading of the position (`review.shortlist`, the
    // same event the board reads), the scan's best move until that reading
    // is in; so each step waits for the shortlist to read the position the
    // board shows, takes the best move it shows then, plays it, and goes on,
    // a fixed stretch or a mate. Playing anything else than the arrow would
    // contradict the board (2026-10-14, after it asked the engine itself at
    // a shallower depth and did). Stops the moment the reader moves the
    // board themselves. While it plays the button is Pause (2026-10-19):
    // a press (or L) stops it where it is; pressed again, Main line plays
    // on from there.
    const lineRun = React.useRef(0);
    const [playing, setPlaying] = React.useState(false);
    const playingRef = React.useRef(false);
    playingRef.current = playing;
    const pauseMainLine = () => {
      lineRun.current++;
      setPlaying(false);
    };
    const arrowBest = (chess: Chess): string | undefined => {
      const state = review!.state();
      const fen = positionAt(state);
      if (!fen) return undefined;
      const live = shortlistRef.current;
      if (live.fen === fen && live.best) return live.best.uci;
      // Where the book knows the position the board shows theory, not the
      // engine: the game's variation, or the nearest to it (theoryMove).
      const book = theoryMove(state, fen);
      if (book) return chess.play(fen, book)?.move.uci;
      // The scan's reading of a game position, as the board falls back to it.
      if (state.exploring || state.browse !== undefined) return undefined;
      return state.scan?.position(state.ply)?.lines[0]?.uci;
    };
    const playMainLine = async () => {
      if (!review) return;
      const mine = ++lineRun.current;
      setPlaying(true);
      try {
        await runMainLine(mine);
      } finally {
        // Over, by its end, the reader's own move or a pause: back to Main line, unless a newer run took over.
        if (mine === lineRun.current) setPlaying(false);
      }
    };
    const runMainLine = async (mine: number) => {
      if (!review) return;
      const chess = await app.getApi<Chess>(CHESS_API);
      if (mine !== lineRun.current) return;
      // The opening as the game played it (2026-10-14): from a game position
      // still in the book, the game's own moves step forward to where it left
      // the book, at the same pace; the arrow's moves follow from there. A
      // game that never left plays to its end and stops.
      const exit = bookExitPly(review.state());
      if (exit !== undefined) {
        while (mine === lineRun.current) {
          const state = review.state();
          if (state.exploring || state.ply >= exit) break;
          review.stepForward();
          await new Promise((r) => setTimeout(r, MAIN_LINE_STEP_MS));
        }
        if (mine !== lineRun.current) return;
        const after = review.state();
        if (!after.exploring && after.ply >= after.game!.moves.length) return; // the whole game was in the book
      }
      for (let i = 0; i < MAIN_LINE_PLIES; i++) {
        const fen = positionAt(review.state());
        if (!fen || mine !== lineRun.current) return;
        const started = Date.now();
        // A floor and then the engine's time (2026-10-19): at least the
        // pace's pause, and until the shortlist's search for this position
        // is over (`settled`: its full depth or its time limit), so each move
        // is the arrow the engine settles on, not one it would still change.
        // Until then it took the arrow about 600 ms in, sometimes at depth
        // 12 or 13 of 14. Theory needs no engine; a search that never
        // settles is cut at MAIN_LINE_WAIT_MS, the move then at the depth reached.
        const settled = () => {
          if (theoryMove(review.state(), fen)) return true;
          const live = shortlistRef.current;
          return live.fen === fen && !!live.settled && !!live.best;
        };
        while (!settled() && Date.now() - started < MAIN_LINE_WAIT_MS) {
          await new Promise((r) => setTimeout(r, 100));
          if (mine !== lineRun.current || positionAt(review.state()) !== fen) return;
        }
        const rest = MAIN_LINE_STEP_MS - (Date.now() - started);
        if (rest > 0) await new Promise((r) => setTimeout(r, rest));
        if (mine !== lineRun.current || positionAt(review.state()) !== fen) return;
        const best = arrowBest(chess);
        if (!best || !review.play(uciToInput(best))) return;
      }
    };
    const perform = React.useCallback(
      (action: StepAction) => {
        if (!review) return;
        if (action === 'puzzle') {
          const right = rightRef.current;
          if (right?.kind === 'mistake') void app.getApi<Puzzles>(PUZZLES_API).then((p) => p.startForMove(right.ply));
          return;
        }
        if (action === 'mainLine') {
          if (playingRef.current) pauseMainLine();
          else if (mainLineRef.current) void playMainLine();
          return;
        }
        if (action === 'outerBack' || action === 'outerForward') {
          const outer = outerRef.current;
          if (outer) review.go(action === 'outerBack' ? outer.back.ply : outer.forward.ply);
          else review[action === 'outerBack' ? 'toStart' : 'toEnd']();
          return;
        }
        review[action]();
      },
      [review],
    );

    React.useEffect(() => {
      if (!review) return;
      const onKey = (event: KeyboardEvent) => {
        const mods = { meta: event.metaKey, ctrl: event.ctrlKey, alt: event.altKey, defaultPrevented: event.defaultPrevented };
        const action = keyAction(event.key, mods, isTyping(event.target as HTMLElement | null), review.state(), rangedRef.current);
        if (!action) return;
        perform(action);
        event.preventDefault();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [review, perform]);

    const onAction = perform;

    return (
      <StepControls view={{ ...view, ready: view.ready && !!review }} onAction={onAction} playing={playing}>
        {/* Beneath the steps: whatever other plugins put at the foot, the material strip first. */}
        <app.Extension name="review.stepControlsFoot" />
      </StepControls>
    );
  };
  app.registerExtension('review.stepControls', Panel);
}
